// Vendor data helpers for the Console — the org vendor book (travel agencies,
// rental companies, ground operators, lodging). Reads `/api/v1/vendors` CRUD,
// the `vendors/get-detail` bundle (vendor row + its named contacts), the
// `vendors/delete` action (which also sweeps the vendor's contacts + invite
// credentials), and the vendor-contact lifecycle actions (create / patch /
// invite / revoke / remove).
//
// The vendor row's own `contactName`/`email`/`phone`/`notes` are the freetext
// BOOKING contact the travel dialog pre-fills from (one per kind ★ default).
// Named on-site people live in `vendor_contacts` and are invitable to connect
// an account (purpose "vendor-invite") — they never join the org.

import { api } from "./api"

export type VendorKind = "agency" | "rental" | "ground" | "lodging" | "other"

export type Vendor = {
  id: string
  kind: VendorKind
  name: string
  contactName: string | null
  email: string | null
  phone: string | null
  notes: string | null
  isDefault: number
}

// Named on-site contact at a vendor. `status` walks pending → invited → active
// (once they redeem the vendor-invite); `revoked` is terminal.
export type VendorContact = {
  id: string
  vendorId: string
  name: string
  title: string | null
  email: string | null
  phone: string | null
  linkedUserId: string | null
  status: "pending" | "invited" | "active" | "revoked"
}

export type VendorDetail = {
  success: true
  vendor: Vendor
  contacts: VendorContact[]
}

export type DeleteVendorDryRun = {
  dryRun: true
  wouldDelete: { id: string; name: string }
  contactCount: number
}

export type DeleteVendorResult = {
  vendorId: string
  deletedContacts: number
}

export const KIND_LABEL: Record<VendorKind, string> = {
  agency: "Travel agency",
  rental: "Rental cars",
  ground: "Ground",
  lodging: "Lodging",
  other: "Other",
}

export function kindLabel(k: string): string {
  return KIND_LABEL[k as VendorKind] ?? "Other"
}

export const VENDOR_KINDS: VendorKind[] = ["agency", "rental", "ground", "lodging", "other"]

// ─── List ─────────────────────────────────────────────────────────────────

export function vendorsQueryOptions() {
  return {
    queryKey: ["resource", "vendors", "all"] as const,
    queryFn: () =>
      api<{ data: Vendor[] }>("/api/v1/vendors", {
        searchParams: { limit: 200, sort: "name", order: "asc" },
      }),
    select: (r: { data: Vendor[] }) => r.data ?? [],
  }
}

export function vendorKey(vendorId: string) {
  return ["vendor-detail", vendorId] as const
}

export function vendorDetailQueryOptions(vendorId: string) {
  return {
    queryKey: vendorKey(vendorId),
    queryFn: () => getVendorDetail(vendorId),
  }
}

export async function getVendorDetail(vendorId: string): Promise<VendorDetail> {
  return api<VendorDetail>("/api/v1/vendors/get-detail", {
    method: "POST",
    body: { id: vendorId },
  })
}

// ─── Vendor row CRUD ────────────────────────────────────────────────────────

// Fields the backend `vendors` table marks updatable (vendors.ts guards).
export type VendorPatch = Partial<
  Pick<Vendor, "kind" | "name" | "contactName" | "email" | "phone" | "notes" | "isDefault">
>

export async function createVendor(input: {
  kind: VendorKind
  name: string
  contactName?: string | null
  email?: string | null
  phone?: string | null
  notes?: string | null
  isDefault?: number
}): Promise<Vendor> {
  return api<Vendor>("/api/v1/vendors", {
    method: "POST",
    body: {
      kind: input.kind,
      name: input.name.trim(),
      contactName: input.contactName?.trim() || undefined,
      email: input.email?.trim() || undefined,
      phone: input.phone?.trim() || undefined,
      notes: input.notes?.trim() || undefined,
      isDefault: input.isDefault ?? 0,
    },
  })
}

export async function patchVendor(vendorId: string, patch: VendorPatch): Promise<Vendor> {
  return api<Vendor>(`/api/v1/vendors/${vendorId}`, { method: "PATCH", body: patch })
}

// Make `vendor` the per-kind default — unsets same-kind siblings first, then
// sets this one. Single-default-per-kind is UI-enforced (the backend allows
// any number; prefill picks the most recently modified on a race).
export async function setVendorDefault(vendor: Vendor, siblings: Vendor[]): Promise<void> {
  for (const sib of siblings) {
    if (sib.id === vendor.id || sib.kind !== vendor.kind || sib.isDefault !== 1) continue
    await patchVendor(sib.id, { isDefault: 0 })
  }
  await patchVendor(vendor.id, { isDefault: 1 })
}

export async function deleteVendor(
  vendorId: string,
  dryRun: true,
): Promise<DeleteVendorDryRun>
export async function deleteVendor(
  vendorId: string,
  dryRun: false,
): Promise<DeleteVendorResult>
export async function deleteVendor(vendorId: string, dryRun: boolean) {
  return api("/api/v1/vendors/delete", {
    method: "POST",
    body: { vendorId, dryRun },
  })
}

// ─── Named contacts ─────────────────────────────────────────────────────────

export async function createVendorContact(input: {
  vendorId: string
  name: string
  title?: string
  email?: string
  phone?: string
}): Promise<VendorContact> {
  const email = input.email?.trim()
  return api<VendorContact>("/api/v1/vendor-contacts", {
    method: "POST",
    body: {
      vendorId: input.vendorId,
      name: input.name.trim(),
      title: input.title?.trim() || undefined,
      email: email || undefined,
      // Lookup/match key — same normalization as invite_tokens.emailNormalized.
      emailNormalized: email ? email.toLowerCase() : undefined,
      phone: input.phone?.trim() || undefined,
    },
  })
}

// Only name / title / phone are updatable on a contact — email is immutable
// (it pairs with the minted invite credential), so changing it means remove +
// re-add.
export async function updateVendorContact(
  contactId: string,
  patch: { name?: string; title?: string | null; phone?: string | null },
): Promise<VendorContact> {
  return api<VendorContact>(`/api/v1/vendor-contacts/${contactId}`, {
    method: "PATCH",
    body: patch,
  })
}

// Vendor invite/revoke were retired — the backend send-invite / revoke actions
// were removed. Vendor contacts remain as plain CRUD records (no account, no
// invite credential). deleteVendorContact stays for removing a contact.

export async function deleteVendorContact(vendorContactId: string): Promise<void> {
  await api("/api/v1/vendor-contacts/delete", {
    method: "POST",
    body: { vendorContactId, dryRun: false },
  })
}
